import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

export interface ExtrasConfirmedPdfItem {
    type_label: string;
    partner_name: string;
    quantity: number;
    start_time?: string;
    end_time?: string;
    confirmed_comment?: string;
}

export interface ExtrasConfirmedPdfPartner {
    name: string;
    phone?: string;
    email?: string;
}

interface ExtrasConfirmedPdfData {
    eventLabel: string;
    eventDate?: string;
    eventStartTime?: string;
    eventEndTime?: string;
    numPeople?: number | null;
    clientPhone?: string;
    clientEmail?: string;
    partners: ExtrasConfirmedPdfPartner[];
    items: ExtrasConfirmedPdfItem[];
    issuerName?: string;
    logoUrl?: string | null;
    brandColor?: string;
    /** false = ne déclenche pas le téléchargement local (`doc.save`), pour un rattachement automatique
     *  silencieux aux Documents de la privatisation. Défaut true (comportement inchangé). */
    download?: boolean;
}

const detectImageFormat = (url: string): 'PNG' | 'JPEG' | 'WEBP' => {
    if (!url) return 'PNG';
    const lower = url.toLowerCase();
    if (lower.includes('image/png') || lower.endsWith('.png')) return 'PNG';
    if (lower.includes('image/webp') || lower.endsWith('.webp')) return 'WEBP';
    if (lower.includes('image/jpg') || lower.includes('image/jpeg') || lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'JPEG';
    return 'PNG';
};

const hexToRgb = (hex?: string): [number, number, number] => {
    const clean = (hex || '#6d28d9').replace('#', '');
    const r = parseInt(clean.substring(0, 2), 16) || 0;
    const g = parseInt(clean.substring(2, 4), 16) || 0;
    const b = parseInt(clean.substring(4, 6), 16) || 0;
    return [r, g, b];
};

const getContrastRgb = (hex?: string): [number, number, number] => {
    const [r, g, b] = hexToRgb(hex);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? [0, 0, 0] : [255, 255, 255];
};

// Assombrit une couleur pour obtenir un second ton de dégradé (miroir du --brand-2 de generateSupplierOrderPdf).
const darken = (rgb: [number, number, number], amount: number): [number, number, number] =>
    rgb.map((c) => Math.max(0, Math.round(c * (1 - amount)))) as [number, number, number];

// Fiche interne récapitulant les extras d'une privatisation une fois VALIDÉS par le(s) prestataire(s) — même
// mise en page que generateSupplierOrderPdf (Commandes) : bandeau dégradé + badge, fiches Prestataire(s)/
// Client côte à côte, date & heure centrée en évidence, tableau des extras (type + horaire réellement
// booké). Uniquement les extras réellement confirmés (jamais les suggestions de personnes, qui ne sont que
// des souhaits, pas des engagements). Demande explicite d'Emeni (2026-08-22) : "ça doit ressembler au récap
// commande [...] avec infos partenaire, infos client, date privat heure, et les types d'extras avec leurs
// heures bookées".
export const generateExtrasConfirmedPdf = async (data: ExtrasConfirmedPdfData) => {
    try {
        const { jsPDF } = await import('jspdf');
        const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

        const primary = hexToRgb(data.brandColor || '#6d28d9');
        const primary2 = darken(primary, 0.28);
        const onPrimary = getContrastRgb(data.brandColor || '#6d28d9');
        const ink: [number, number, number] = [34, 25, 51];
        const inkFaint: [number, number, number] = [138, 131, 160];
        const tint: [number, number, number] = [244, 241, 252];
        const stripe: [number, number, number] = [250, 248, 253];
        const border: [number, number, number] = [228, 223, 242];

        // ── Bandeau d'en-tête (dégradé simulé + badge) ──────────────────────
        const headerH = 34;
        const bands = 60;
        for (let i = 0; i < bands; i++) {
            const t = i / (bands - 1);
            const r = Math.round(primary[0] + (primary2[0] - primary[0]) * t);
            const g = Math.round(primary[1] + (primary2[1] - primary[1]) * t);
            const b = Math.round(primary[2] + (primary2[2] - primary[2]) * t);
            doc.setFillColor(r, g, b);
            doc.rect((210 / bands) * i, 0, 210 / bands + 0.5, headerH, 'F');
        }

        let logoDrawW = 0;
        if (data.logoUrl) {
            const maxW = 24, maxH = 15;
            let drawW = maxW, drawH = maxH;
            try {
                const props = doc.getImageProperties(data.logoUrl);
                const ratio = props.width / props.height;
                if (ratio > maxW / maxH) { drawW = maxW; drawH = maxW / ratio; }
                else { drawH = maxH; drawW = maxH * ratio; }
            } catch (e) { /* repli sur le texte seul */ }
            try {
                doc.addImage(data.logoUrl, detectImageFormat(data.logoUrl), 15, 9, drawW, drawH);
                logoDrawW = drawW + 8;
            } catch (e) { console.warn('Could not render logo in PDF:', e); }
        }

        doc.setTextColor(onPrimary[0], onPrimary[1], onPrimary[2]);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(18);
        doc.text((data.issuerName || 'Récapitulatif').toUpperCase(), 15 + logoDrawW, 21);

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8.5);
        const badgeLabel = 'EXTRAS CONFIRMÉS';
        const badgeW = doc.getTextWidth(badgeLabel) + 12;
        doc.setFillColor(255, 255, 255);
        doc.roundedRect(195 - badgeW, 12, badgeW, 9, 4.5, 4.5, 'F');
        doc.setTextColor(primary2[0], primary2[1], primary2[2]);
        doc.text(badgeLabel, 195 - badgeW / 2, 17.7, { align: 'center' });

        // ── Fiches Prestataire(s) / Client côte à côte ──────────────────────
        let y = headerH + 15;
        const colW = 87;
        const leftX = 15, leftValX = 15 + 22;
        const rightX = 15 + colW + 6, rightValX = rightX + 22;

        const partners = data.partners.length > 0 ? data.partners : [{ name: 'Prestataire' }];
        const partnerRows: [string, string][] = [];
        if (partners.length === 1) {
            partnerRows.push(['Enseigne', partners[0].name || '']);
            if (partners[0].phone) partnerRows.push(['Téléphone', partners[0].phone]);
            if (partners[0].email) partnerRows.push(['Email', partners[0].email]);
        } else {
            partners.forEach((p, i) => partnerRows.push([i === 0 ? 'Enseignes' : '', p.name]));
        }

        const clientRows: [string, string][] = [['Nom', data.eventLabel || '']];
        if (data.clientPhone) clientRows.push(['Téléphone', data.clientPhone]);
        if (data.clientEmail) clientRows.push(['Email', data.clientEmail]);
        if (data.numPeople) clientRows.push(['Personnes', String(data.numPeople)]);

        const cardH = Math.max(partnerRows.length, clientRows.length) * 8.5 + 14;

        // Carte Prestataire(s) (neutre)
        doc.setLineWidth(0.2);
        doc.setFillColor(stripe[0], stripe[1], stripe[2]);
        doc.roundedRect(leftX, y, colW, cardH, 3, 3, 'F');
        doc.setDrawColor(border[0], border[1], border[2]);
        doc.roundedRect(leftX, y, colW, cardH, 3, 3, 'D');

        // Carte Client (mise en valeur — même traitement que la carte Client de generateSupplierOrderPdf)
        const highlightBorder = darken(primary, 0.15);
        doc.setFillColor(tint[0], tint[1], tint[2]);
        doc.roundedRect(rightX, y, colW, cardH, 3, 3, 'F');
        doc.setLineWidth(0.8);
        doc.setDrawColor(highlightBorder[0], highlightBorder[1], highlightBorder[2]);
        doc.roundedRect(rightX, y, colW, cardH, 3, 3, 'D');
        doc.setLineWidth(0.2);

        let cardTitleY = y + 9;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8.5);
        doc.setTextColor(primary2[0], primary2[1], primary2[2]);
        doc.text(partners.length === 1 ? 'PRESTATAIRE' : 'PRESTATAIRES', leftX + 6, cardTitleY);
        doc.text('CLIENT', rightX + 6, cardTitleY);
        doc.setDrawColor(border[0], border[1], border[2]);
        doc.line(leftX + 6, cardTitleY + 2, leftX + colW - 6, cardTitleY + 2);
        doc.line(rightX + 6, cardTitleY + 2, rightX + colW - 6, cardTitleY + 2);

        let rowY = cardTitleY + 8.5;
        partnerRows.forEach(([label, value]) => {
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8);
            doc.setTextColor(inkFaint[0], inkFaint[1], inkFaint[2]);
            doc.text(label, leftX + 6, rowY);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            doc.setTextColor(ink[0], ink[1], ink[2]);
            doc.text(doc.splitTextToSize(value, colW - 14), leftValX, rowY);
            rowY += 8.5;
        });

        rowY = cardTitleY + 8.5;
        clientRows.forEach(([label, value]) => {
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8);
            doc.setTextColor(inkFaint[0], inkFaint[1], inkFaint[2]);
            doc.text(label, rightX + 6, rowY);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            doc.setTextColor(ink[0], ink[1], ink[2]);
            doc.text(doc.splitTextToSize(value, colW - 14), rightValX, rowY);
            rowY += 8.5;
        });

        y += cardH + 10;

        // ── Date & heure de la privatisation — centrée, en évidence ─────────
        if (data.eventDate) {
            const dateLabel = format(new Date(`${data.eventDate}T00:00:00`), 'EEEE d MMMM yyyy', { locale: fr }).toUpperCase();
            const fullLabel = (data.eventStartTime || data.eventEndTime)
                ? `${dateLabel} DE ${data.eventStartTime || '?'} À ${data.eventEndTime || '?'}`
                : dateLabel;
            const barH = 16;
            doc.setFillColor(tint[0], tint[1], tint[2]);
            doc.roundedRect(15, y, 180, barH, 3, 3, 'F');
            doc.setDrawColor(border[0], border[1], border[2]);
            doc.roundedRect(15, y, 180, barH, 3, 3, 'D');
            doc.setFillColor(primary[0], primary[1], primary[2]);
            doc.rect(15, y, 180, 1.4, 'F');
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(7.5);
            doc.setTextColor(primary2[0], primary2[1], primary2[2]);
            doc.text('DATE & HEURE DE LA PRIVATISATION', 105, y + 6.5, { align: 'center' });
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(12);
            doc.setTextColor(ink[0], ink[1], ink[2]);
            doc.text(fullLabel, 105, y + 12.5, { align: 'center' });
            y += barH + 10;
        }

        // ── Extras confirmés ─────────────────────────────────────────────────
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10.5);
        doc.setTextColor(ink[0], ink[1], ink[2]);
        doc.text('DÉTAILS DES EXTRAS CONFIRMÉS', 15, y);
        y += 7;

        const showPartnerCol = partners.length > 1;
        doc.setFillColor(tint[0], tint[1], tint[2]);
        doc.rect(15, y - 4.5, 180, 8, 'F');
        doc.setTextColor(primary2[0], primary2[1], primary2[2]);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8.5);
        doc.text('EXTRA', 20, y);
        if (showPartnerCol) doc.text('PRESTATAIRE', 90, y);
        doc.text('HORAIRE BOOKÉ', showPartnerCol ? 140 : 100, y);
        doc.text('QTÉ', 190, y, { align: 'right' });
        y += 8;

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9.5);
        data.items.forEach((it, idx) => {
            const commentLines = it.confirmed_comment ? doc.splitTextToSize(it.confirmed_comment, 68) : [];
            const rowH = Math.max(8, 6 + commentLines.length * 4.2);
            if (y + rowH > 270) { doc.addPage(); y = 20; }
            if (idx % 2 === 0) {
                doc.setFillColor(stripe[0], stripe[1], stripe[2]);
                doc.rect(15, y - 5, 180, rowH, 'F');
            }
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(ink[0], ink[1], ink[2]);
            doc.text(it.type_label, 20, y);
            if (showPartnerCol) {
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(9);
                doc.setTextColor(inkFaint[0], inkFaint[1], inkFaint[2]);
                doc.text(it.partner_name || '', 90, y);
                doc.setFontSize(9.5);
            }
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(ink[0], ink[1], ink[2]);
            const horaire = (it.start_time || it.end_time) ? `${it.start_time || '?'} - ${it.end_time || '?'}` : '-';
            doc.text(horaire, showPartnerCol ? 140 : 100, y);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(primary[0], primary[1], primary[2]);
            doc.text(`x ${it.quantity}`, 193, y, { align: 'right' });
            if (commentLines.length > 0) {
                doc.setFont('helvetica', 'italic');
                doc.setFontSize(8);
                doc.setTextColor(inkFaint[0], inkFaint[1], inkFaint[2]);
                doc.text(commentLines, 20, y + 4.5);
            }
            y += rowH;
        });

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6.5);
        doc.setTextColor(inkFaint[0], inkFaint[1], inkFaint[2]);
        doc.text(`Document généré le ${format(new Date(), 'dd/MM/yyyy à HH:mm')} — iAmani Connexion`, 105, 290, { align: 'center' });

        const fileName = `Extras_confirmes_${(data.eventLabel || 'privatisation').replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
        // `download: false` sert au rattachement automatique en arrière-plan (dès qu'un lot est confirmé,
        // sans que quelqu'un ait cliqué sur le bouton) — pas de téléchargement local intempestif dans ce cas,
        // seul le rattachement aux Documents de la privatisation compte. Demande d'Emeni (2026-08-22).
        if (data.download !== false) doc.save(fileName);
        const base64 = doc.output('datauristring').split(',')[1] || '';
        return { base64, fileName, mimeType: 'application/pdf' };
    } catch (e) {
        console.error('Extras Confirmed PDF Generation Error:', e);
        alert('Erreur lors de la génération du document PDF.');
        return null;
    }
};
